import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Auth from 'j-toker';

import Select from 'components/ui/SelectField';
import DatePicker from 'components/ui/DatePicker';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import Col from 'components/ui/Col';

import { media } from 'helpers/MediaHelper';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { ButtonPrimary } from '@yuhu-inc/yuhui/dist';

const PARTS_NEEDED = 'Parts Needed';
const PARTS_NEEDED_REVIEW = 'Parts Needed Review';
const CONTRACTOR_NEEDED = 'Contractor Needed';
const CONTRACTOR_NEEDED_REVIEW = 'Contractor Needed Review';

const StyledGeneratePDF = styled.div`
  position: relative;
  justify-content: flex-end;
  display: inline;
  float: right;
`;

const StyledGeneratePDFButton = styled(ButtonPrimary)`
  height: 29px !important;
  line-height: 29px !important;
  padding: 0px 18px !important;
`;
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const StyledRow = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const StyledItem = styled.div`
  width: 100%;
  text-align: ${(props) => props.textAlign || 'left'};
  margin: ${(props) => props.margin || '0px'};
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const StyledItemLabel = styled.div`
  margin-bottom: 5px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

const StyledError = styled.div`
  color: ${colors.YUHU_RED};
`;

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const PartsContractorNeeded = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  mode,
  downloadPdf,
}) => {
  const partsNeeded = values.status === PARTS_NEEDED || values.status === PARTS_NEEDED_REVIEW;
  const contractorNeeded =
    values.status === CONTRACTOR_NEEDED || values.status === CONTRACTOR_NEEDED_REVIEW;

  const userIsMaintenance = Auth.user.type === 'maintenance';

  if (!partsNeeded && !contractorNeeded) return null;

  let title = getTranslatedString('landlordPortal.maintenance.tickets.contractorNeeded');
  let responsibleText = getTranslatedString(
    'landlordPortal.maintenance.tickets.whoIsResponsibleContractor'
  );
  let kindOfText = getTranslatedString('landlordPortal.maintenance.tickets.whatKindContractor');
  let kindOfPlaceholder = getTranslatedString(
    'landlordPortal.maintenance.tickets.whatKindContractorPlaceholder'
  );
  if (partsNeeded) {
    title = getTranslatedString('landlordPortal.maintenance.tickets.partsNeeded');
    responsibleText = getTranslatedString(
      'landlordPortal.maintenance.tickets.whoIsResponsibleParts'
    );
    kindOfText = getTranslatedString('landlordPortal.maintenance.tickets.whatKindPart');
    kindOfPlaceholder = getTranslatedString(
      'landlordPortal.maintenance.tickets.whatKindPartPlaceholder'
    );
  }

  return (
    <React.Fragment>
      <StyledContainer>
        <Spacer mb={0.3}>
          <FormTitle>{title}</FormTitle>
          <StyledGeneratePDF mode={mode}>
            <StyledGeneratePDFButton
              type="button"
              onClick={() => downloadPdf(values.id)}
              contentKey="generatePDF"
            />
          </StyledGeneratePDF>
        </Spacer>
        <StyledRow>
          <StyledItem>
            <StyledItemLabel>{responsibleText}</StyledItemLabel>
            <Select
              name="responsible_for_action"
              showSearch
              placeholder={getTranslatedString(
                'landlordPortal.maintenance.tickets.responsibleSelect'
              )}
              size="large"
              value={values.responsible_for_action}
              onChange={(value) => {
                setFieldValue('responsible_for_action', value);
                setFieldValue('proposed_scheduled_at', undefined);
              }}
              disabled={!userIsMaintenance}
            >
              <Select.Option value="landlord">
                {getTranslatedString('landlordPortal.maintenance.tickets.responsibleLandlord')}
              </Select.Option>
              <Select.Option value="pic">
                {getTranslatedString('landlordPortal.maintenance.tickets.responsiblePic')}
              </Select.Option>
            </Select>
            {errors.responsible_for_action && touched.responsible_for_action && (
              <StyledError>{errors.responsible_for_action}</StyledError>
            )}
          </StyledItem>
        </StyledRow>
        <StyledRow>
          <Col span={24}>
            <Form.Item
              label={kindOfText}
              validateStatus={
                touched.contractor_part_type && errors.contractor_part_type ? 'error' : null
              }
              help={touched.contractor_part_type && errors.contractor_part_type}
            >
              <FullWidthTextArea
                placeholder={kindOfPlaceholder}
                size="large"
                name="contractor_part_type"
                value={values.contractor_part_type}
                onChange={handleChange}
                disabled={!userIsMaintenance}
              />
            </Form.Item>
          </Col>
        </StyledRow>

        <StyledRow>
          <StyledItem>
            <StyledItemLabel>
              {getTranslatedString('landlordPortal.maintenance.tickets.recommendedScheduleDate')}
            </StyledItemLabel>
            <Form.Item>
              <StyledDatePicker
                size="large"
                value={values.proposed_scheduled_at}
                defaultPickerValue={moment().locale(getCurrentLocale())}
                showToday={false}
                placeholder="e.g. 2020-01-01"
                mobileMode="date"
                onChange={(value) => setFieldValue('proposed_scheduled_at', value)}
                disabled={
                  !userIsMaintenance ||
                  !values.responsible_for_action ||
                  values.responsible_for_action === 'landlord'
                }
              />
            </Form.Item>
          </StyledItem>
        </StyledRow>
      </StyledContainer>
    </React.Fragment>
  );
};

export default PartsContractorNeeded;
